import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { saveSyncRecord, getAutoSyncSettings, saveAutoSyncSettings, SyncRecord, AutoSyncSettings } from '@/lib/storage';
import { generateId } from '@/lib/storage';

interface SyncButtonProps {
  sessionId: string;
  onSync: () => Promise<{ success: boolean; message: string }>;
}

export function SyncButton({ sessionId, onSync }: SyncButtonProps) {
  const [isSyncing, setIsSyncing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(false);
  const [syncResult, setSyncResult] = useState<{ success: boolean; message: string } | null>(null);

  const toggleAutoSync = async () => {
    const settings = await getAutoSyncSettings();
    const newSettings: AutoSyncSettings = {
      ...settings,
      enabled: !settings.enabled,
    };
    await saveAutoSyncSettings(newSettings);
    setAutoSyncEnabled(newSettings.enabled);
  };

  const handleSync = async () => {
    setIsSyncing(true);
    setSyncResult(null);
    setShowConfirm(false);

    try {
      const result = await onSync();
      
      const record: SyncRecord = {
        id: generateId(),
        source: 'local',
        target: 'hermes',
        time: Date.now(),
        summary: result.message,
        result: result.success ? 'success' : 'failed',
        details: { sessionId },
      };
      await saveSyncRecord(record);

      setSyncResult(result);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Sync failed';
      
      const record: SyncRecord = {
        id: generateId(),
        source: 'local',
        target: 'hermes',
        time: Date.now(),
        summary: errorMessage,
        result: 'failed',
        details: { sessionId, error: errorMessage },
      };
      await saveSyncRecord(record);

      setSyncResult({ success: false, message: errorMessage });
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      {/* Auto-sync toggle */}
      <div className="relative group">
        <button
          onClick={toggleAutoSync}
          disabled={isSyncing}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all duration-200 ${
            autoSyncEnabled
              ? 'bg-accent-primary/10 text-accent-primary border border-accent-primary/30'
              : 'bg-surface text-text-secondary border border-border hover:bg-surface-hover hover:text-text-primary'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
          title={autoSyncEnabled ? 'Auto-sync enabled (click to disable)' : 'Auto-sync disabled (click to enable)'}
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <span>Auto</span>
        </button>
        <AnimatePresence>
          {autoSyncEnabled && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs bg-text-primary text-surface rounded shadow-lg whitespace-nowrap"
            >
              Auto-sync ON
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Manual sync button */}
      <button
        onClick={() => setShowConfirm(true)}
        disabled={isSyncing}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all duration-200 ${
          isSyncing
            ? 'bg-accent-primary/10 text-accent-primary border border-accent-primary/30 cursor-wait'
            : 'bg-surface text-text-secondary border border-border hover:bg-surface-hover hover:text-text-primary hover:border-border/50'
        } disabled:opacity-50`}
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        <span>{isSyncing ? 'Syncing...' : 'Sync'}</span>
      </button>

      {/* Risk confirmation dialog */}
      <AnimatePresence>
        {showConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            onClick={() => setShowConfirm(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="w-full max-w-md bg-surface rounded-2xl border border-border p-6 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                  <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-text-primary">Confirm Sync</h3>
                  <p className="text-sm text-text-secondary">This will push local changes to Hermes.</p>
                </div>
              </div>

              <div className="mb-4 p-3 rounded-xl bg-surface border border-border text-sm text-text-secondary">
                <div className="font-medium text-text-primary mb-1">Risk Level: Low</div>
                <div>Synchronizing session data only. No file modifications or deployments.</div>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowConfirm(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSync}
                  disabled={isSyncing}
                  className="btn-primary"
                >
                  {isSyncing ? 'Syncing...' : 'Confirm Sync'}
                </button>
              </div>

              {/* Sync result toast */}
              <AnimatePresence>
                {syncResult && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className={`mt-4 p-3 rounded-xl text-sm ${
                      syncResult.success
                        ? 'bg-green-500/10 border border-green-500/30 text-green-400'
                        : 'bg-red-500/10 border border-red-500/30 text-red-400'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {syncResult.success ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        )}
                      </svg>
                      <span>{syncResult.message}</span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}